/* eslint-disable no-restricted-syntax */
/* eslint-disable no-console */
type SoundType = 'call-start' | 'call-end' | 'connect' | 'disconnect' | 'ringing';

/** 'classic' — 기존 사인톤(쿠팡 기본). 'delight' — 벨 음색의 상승/하강 대구 모티프. */
type SoundTheme = 'classic' | 'delight';

interface AudioOptions {
  frequency?: number;
  duration?: number;
  volume?: number;
  type?: OscillatorType;
  fadeOut?: boolean;
}

class AudioSynthesizer {
  private audioContext: AudioContext | null = null;
  private theme: SoundTheme = 'delight';
  private ringingIntervalId: ReturnType<typeof setInterval> | null = null;
  private connectSoundTimeouts: ReturnType<typeof setTimeout>[] = [];
  private isRingingActive: boolean = false;
  private activeRingingNodes: { oscillator: OscillatorNode; gainNode: GainNode }[] = [];

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  getExistingAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  private async resumeAudioContext(): Promise<void> {
    const context = this.getAudioContext();
    if (context.state === 'suspended') {
      await context.resume();
    }
  }

  private async playTone(options: AudioOptions): Promise<void> {
    await this.resumeAudioContext();

    const context = this.getAudioContext();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.frequency.setValueAtTime(options.frequency || 440, context.currentTime);
    oscillator.type = options.type || 'sine';

    const volume = Math.min(Math.max(options.volume || 0.3, 0), 1);
    const duration = options.duration || 0.5;
    const fadeOut = options.fadeOut !== false; // default to true

    gainNode.gain.setValueAtTime(0, context.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, context.currentTime + 0.01);

    if (fadeOut) {
      gainNode.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
    } else {
      gainNode.gain.setValueAtTime(volume, context.currentTime + duration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0.001, context.currentTime + duration);
    }

    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + duration);
  }

  private async playRingingToneTracked(options: AudioOptions): Promise<void> {
    await this.resumeAudioContext();

    const context = this.getAudioContext();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.frequency.setValueAtTime(options.frequency || 440, context.currentTime);
    oscillator.type = options.type || 'sine';

    const volume = Math.min(Math.max(options.volume || 0.3, 0), 1);
    const duration = options.duration || 0.5;

    // Setup gain without fadeout for ringing
    gainNode.gain.setValueAtTime(0, context.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, context.currentTime + 0.01);
    gainNode.gain.setValueAtTime(volume, context.currentTime + duration - 0.01);
    gainNode.gain.linearRampToValueAtTime(0.001, context.currentTime + duration);

    // Track this node for potential forced stop
    this.activeRingingNodes.push({ oscillator, gainNode });

    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + duration);

    // Remove from tracking when finished
    oscillator.onended = () => {
      const index = this.activeRingingNodes.findIndex((node) => node.oscillator === oscillator);
      if (index !== -1) {
        this.activeRingingNodes.splice(index, 1);
      }
    };
  }

  setTheme(theme: SoundTheme): void {
    this.theme = theme;
  }

  /**
   * 사용자 제스처 핸들러 안에서 "동기로" 호출할 것 — AudioContext를 미리 깨워
   * 이후(네트워크 뒤) 재생되는 사운드의 유실/타이밍 뭉개짐을 방지한다.
   */
  unlockAudio(): void {
    const context = this.getAudioContext();
    if (context.state === 'suspended') void context.resume();
  }

  /** 버튼 즉각 피드백 — 가볍고 짧은 탭. 제스처 안에서 동기 스케줄링된다. */
  playTapSound(): void {
    try {
      this.unlockAudio();
      if (this.theme === 'delight') {
        this.playChime(1046.5, 0, { volume: 0.07, decay: 0.22, pan: 0 }); // C6 '톡'
      } else {
        void this.playTone({ frequency: 900, duration: 0.06, volume: 0.12, type: 'sine' });
      }
    } catch (error) {
      console.warn('Failed to play tap sound:', error);
    }
  }

  /** 뮤트 토글 피드백 — 짧은 두 음 (뮤트: 하강 / 해제: 상승). 딜라이트 모티프의 축소판 */
  playMuteSound(muted: boolean): void {
    try {
      this.unlockAudio();
      if (this.theme === 'delight') {
        const [first, second] = muted ? [1046.5, 784.0] : [784.0, 1046.5]; // C6↘G5 / G5↗C6
        this.playChime(first, 0, { volume: 0.05, decay: 0.14, pan: -0.08 });
        this.playChime(second, 0.07, { volume: 0.055, decay: 0.2, pan: 0.08 });
      } else {
        void this.playTone({ frequency: muted ? 600 : 900, duration: 0.06, volume: 0.1, type: 'sine' });
      }
    } catch (error) {
      console.warn('Failed to play mute sound:', error);
    }
  }

  /**
   * 벨/첼레스타풍의 풍부한 단음 — 디튠된 기음 페어(코러스감) + 옥타브 + 12도 배음을
   * 로우패스와 스테레오 패닝을 거쳐 재생한다. Web Audio 스케줄링이라 샘플 정확.
   */
  private playChime(
    frequency: number,
    startDelaySec: number,
    opts: { volume: number; decay: number; pan: number },
  ): { oscillators: OscillatorNode[]; master: GainNode } {
    const context = this.getAudioContext();
    const t0 = context.currentTime + startDelaySec;
    const oscillators: OscillatorNode[] = [];

    const master = context.createGain();
    master.gain.value = opts.volume;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3800;
    filter.Q.value = 0.5;
    const panner = context.createStereoPanner();
    panner.pan.value = opts.pan;
    master.connect(filter);
    filter.connect(panner);
    panner.connect(context.destination);

    const partials: Array<[number, number]> = [
      [frequency * Math.pow(2, -4 / 1200), 0.5], // 기음 -4센트
      [frequency * Math.pow(2, 4 / 1200), 0.5], // 기음 +4센트 — 페어가 살짝 어긋나며 따뜻한 코러스
      [frequency * 2, 0.35], // 옥타브
      [frequency * 3, 0.1], // 12도
    ];
    partials.forEach(([freq, amp]) => {
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = freq;
      gainNode.gain.setValueAtTime(0, t0);
      gainNode.gain.linearRampToValueAtTime(amp, t0 + 0.012);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.decay);
      oscillator.connect(gainNode);
      gainNode.connect(master);
      oscillator.start(t0);
      oscillator.stop(t0 + opts.decay + 0.05);
      oscillators.push(oscillator);
    });

    return { oscillators, master };
  }

  /**
   * delight 링톤 — 오브 레포의 링백(지속음 버스트, 1초 on / 3초 주기)을 가공:
   * C5+E5 다이애드 험이 숨처럼 차올랐다 가라앉는다. 연결음(G5→C6)이 이 화음을
   * 완성하는 짝 구조라, 기다림이 연결로 자연스럽게 해소된다.
   */
  private async playDelightRingingTone(): Promise<void> {
    try {
      await this.resumeAudioContext();
      const notes = [
        this.playHum(523.25, 1.0, { volume: 0.05, pan: -0.15 }),
        this.playHum(659.25, 1.0, { volume: 0.055, pan: 0.15 }),
      ];
      // 연결 순간 즉시 끊을 수 있도록 링잉 노드로 추적
      notes.forEach(({ oscillators, master }) =>
        oscillators.forEach((oscillator) => {
          this.activeRingingNodes.push({ oscillator, gainNode: master });
          oscillator.onended = () => {
            const index = this.activeRingingNodes.findIndex((n) => n.oscillator === oscillator);
            if (index !== -1) this.activeRingingNodes.splice(index, 1);
          };
        }),
      );
    } catch (error) {
      console.warn('Failed to play ringing tone:', error);
    }
  }

  /**
   * 지속형 험(hum) — delight-voice-orb의 링백 버스트(느린 어택→서스테인→릴리즈)를
   * 차임과 같은 배음 구성으로 가공한 것. 링톤용이라 노드를 반환해 추적 가능하게 한다.
   */
  private playHum(
    frequency: number,
    durationSec: number,
    opts: { volume: number; pan: number },
  ): { oscillators: OscillatorNode[]; master: GainNode } {
    const context = this.getAudioContext();
    const t0 = context.currentTime;
    const oscillators: OscillatorNode[] = [];

    const master = context.createGain();
    master.gain.value = opts.volume;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3200;
    filter.Q.value = 0.5;
    const panner = context.createStereoPanner();
    panner.pan.value = opts.pan;
    master.connect(filter);
    filter.connect(panner);
    panner.connect(context.destination);

    const partials: Array<[number, number]> = [
      [frequency * Math.pow(2, -4 / 1200), 0.5],
      [frequency * Math.pow(2, 4 / 1200), 0.5],
      [frequency * 2, 0.18],
    ];
    partials.forEach(([freq, amp]) => {
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = freq;
      gainNode.gain.setValueAtTime(0, t0);
      gainNode.gain.linearRampToValueAtTime(amp, t0 + 0.18); // 느린 어택 — 벨이 아니라 숨처럼
      gainNode.gain.setValueAtTime(amp, t0 + durationSec - 0.3);
      gainNode.gain.linearRampToValueAtTime(0.0001, t0 + durationSec);
      oscillator.connect(gainNode);
      gainNode.connect(master);
      oscillator.start(t0);
      oscillator.stop(t0 + durationSec + 0.05);
      oscillators.push(oscillator);
    });

    return { oscillators, master };
  }

  /**
   * delight 연결음 — C5→G5, 으뜸음에서 딸림음으로 열리는 완전5도 상행 확인음.
   * 링톤(C5+E5 험)에서 자연스럽게 이어지고, 종료음의 정확한 미러가 된다.
   */
  private async playDelightConnectSound(): Promise<void> {
    await this.resumeAudioContext();
    this.playChime(523.25, 0, { volume: 0.15, decay: 0.7, pan: -0.15 });
    this.playChime(783.99, 0.16, { volume: 0.17, decay: 1.2, pan: 0.15 });
  }

  /**
   * delight 종료음 — G5→C5, 연결음의 미러(완전5도 하강). 마지막 C5가
   * 길게 내려앉으며 으뜸음으로 귀결 — 글라이드 없이 이산 차임만 사용.
   */
  private async playDelightEndSound(): Promise<void> {
    await this.resumeAudioContext();
    this.playChime(783.99, 0, { volume: 0.15, decay: 0.6, pan: 0.15 });
    this.playChime(523.25, 0.18, { volume: 0.16, decay: 1.5, pan: -0.15 });
  }

  private stopActiveRingingNodes(): void {
    this.activeRingingNodes.forEach(({ oscillator, gainNode }) => {
      try {
        const context = this.getAudioContext();
        // Immediately cut the volume to stop the sound
        gainNode.gain.cancelScheduledValues(context.currentTime);
        gainNode.gain.setValueAtTime(0, context.currentTime);

        // Stop the oscillator
        oscillator.stop();
      } catch (error) {
        // Ignore errors when stopping already stopped oscillators
      }
    });
    this.activeRingingNodes = [];
  }

  async playCallStartSound(): Promise<void> {
    try {
      // delight: 클릭 탭 사운드가 시작 피드백을 대신한다 — 구식 blip은 생략
      if (this.theme === 'delight') return;
      await this.resumeAudioContext();

      this.playTone({
        frequency: 800,
        duration: 0.15,
        volume: 0.25,
        type: 'sine',
      });

      setTimeout(() => {
        this.playTone({
          frequency: 1200,
          duration: 0.15,
          volume: 0.25,
          type: 'sine',
        });
      }, 100);
    } catch (error) {
      console.warn('Failed to play call start sound:', error);
    }
  }

  async playCallEndSound(): Promise<void> {
    try {
      if (this.theme === 'delight') return await this.playDelightEndSound();
      await this.resumeAudioContext();

      this.playTone({
        frequency: 1000,
        duration: 0.2,
        volume: 0.25,
        type: 'sine',
      });

      setTimeout(() => {
        this.playTone({
          frequency: 600,
          duration: 0.3,
          volume: 0.25,
          type: 'sine',
        });
      }, 150);
    } catch (error) {
      console.warn('Failed to play call end sound:', error);
    }
  }

  private clearConnectSoundTimeouts(): void {
    this.connectSoundTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.connectSoundTimeouts = [];
  }

  async playConnectSound(): Promise<void> {
    try {
      // Clear any existing connect sound timeouts
      this.clearConnectSoundTimeouts();

      if (this.theme === 'delight') return await this.playDelightConnectSound();

      await this.resumeAudioContext();

      this.playTone({
        frequency: 523, // C5
        duration: 0.12,
        volume: 0.22,
        type: 'sine',
      });

      const timeout1 = setTimeout(() => {
        this.playTone({
          frequency: 659, // E5
          duration: 0.12,
          volume: 0.25,
          type: 'sine',
        });
      }, 80);
      this.connectSoundTimeouts.push(timeout1);

      const timeout2 = setTimeout(() => {
        this.playTone({
          frequency: 784, // G5
          duration: 0.12,
          volume: 0.27,
          type: 'sine',
        });
      }, 160);
      this.connectSoundTimeouts.push(timeout2);

      const timeout3 = setTimeout(() => {
        this.playTone({
          frequency: 1047, // C6
          duration: 0.18,
          volume: 0.25,
          type: 'sine',
        });
      }, 240);
      this.connectSoundTimeouts.push(timeout3);
    } catch (error) {
      console.warn('Failed to play connect sound:', error);
    }
  }

  async playDisconnectSound(): Promise<void> {
    try {
      await this.playTone({
        frequency: 400,
        duration: 0.4,
        volume: 0.2,
        type: 'sawtooth',
      });
    } catch (error) {
      console.warn('Failed to play disconnect sound:', error);
    }
  }

  private async playRingingTone(): Promise<void> {
    try {
      await this.resumeAudioContext();

      this.playRingingToneTracked({
        frequency: 480,
        duration: 2.0,
        volume: 0.15,
        type: 'sine',
      });

      setTimeout(() => {
        this.playRingingToneTracked({
          frequency: 440,
          duration: 2.0,
          volume: 0.15,
          type: 'sine',
        });
      }, 50);
    } catch (error) {
      console.warn('Failed to play ringing tone:', error);
    }
  }

  startRinging(): void {
    this.stopRinging();
    this.isRingingActive = true;

    const tone = this.theme === 'delight' ? () => this.playDelightRingingTone() : () => this.playRingingTone();
    const cadenceMs = this.theme === 'delight' ? 3000 : 4000;

    tone();

    this.ringingIntervalId = setInterval(() => {
      if (this.isRingingActive) {
        tone();
      }
    }, cadenceMs);
  }

  stopRinging(): void {
    this.isRingingActive = false;
    this.stopActiveRingingNodes(); // Immediately stop any active ringing sounds
    if (this.ringingIntervalId) {
      clearInterval(this.ringingIntervalId);
      this.ringingIntervalId = null;
    }
  }

  stopRingingAndPlayConnect(): Promise<void> {
    return new Promise((resolve) => {
      this.stopRinging();

      // Wait a short delay to ensure ringing has completely stopped
      setTimeout(async () => {
        await this.playConnectSound();
        resolve();
      }, 100);
    });
  }

  async playSound(type: SoundType): Promise<void> {
    switch (type) {
      case 'call-start':
        return this.playCallStartSound();
      case 'call-end':
        return this.playCallEndSound();
      case 'connect':
        return this.playConnectSound();
      case 'disconnect':
        return this.playDisconnectSound();
      case 'ringing':
        this.startRinging();
        break;
      default:
        break;
    }
  }

  async cleanup(): Promise<void> {
    this.stopRinging();
    this.clearConnectSoundTimeouts();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close();
      this.audioContext = null;
    }
  }
}

/** @knipignore */
export const audioSynthesizer = new AudioSynthesizer();

export const setSoundTheme = (theme: SoundTheme) => audioSynthesizer.setTheme(theme);
export const unlockAudio = () => audioSynthesizer.unlockAudio();
export const getUiAudioContext = () => audioSynthesizer.getExistingAudioContext();
export const playTapSound = () => audioSynthesizer.playTapSound();
export const playMuteSound = (muted: boolean) => audioSynthesizer.playMuteSound(muted);
export const playCallStartSound = () => audioSynthesizer.playCallStartSound();
export const playCallEndSound = () => audioSynthesizer.playCallEndSound();
/** @knipignore */
export const playConnectSound = () => audioSynthesizer.playConnectSound();
/** @knipignore */
export const playDisconnectSound = () => audioSynthesizer.playDisconnectSound();
export const startRinging = () => audioSynthesizer.startRinging();
export const stopRinging = () => audioSynthesizer.stopRinging();
export const stopRingingAndPlayConnect = () => audioSynthesizer.stopRingingAndPlayConnect();
/** @knipignore */
export const playSound = (type: SoundType) => audioSynthesizer.playSound(type);
